import { z } from 'zod'

/** Response shapes shared by the API (producer) and the web app (consumer, validated at runtime). */

export const healthSchema = z.object({
  ok: z.boolean(),
  version: z.string(),
  db: z.enum(['ok', 'error']),
})
export type Health = z.output<typeof healthSchema>

export const publicConfigSchema = z.object({
  version: z.string(),
  testMode: z.boolean(),
  google: z.boolean(),
})
export type PublicConfig = z.output<typeof publicConfigSchema>

export const meSchema = z.object({
  user: z.object({
    id: z.uuid(),
    name: z.string(),
    email: z.string(),
    image: z.string().nullable(),
  }),
  /** The user's current time (moves with the test clock in test mode). */
  now: z.iso.datetime(),
  clockOffsetMs: z.number(),
  testMode: z.boolean(),
  version: z.string(),
  /**
   * Where the user's change feed stood when this was loaded (the newest event's seq). The live
   * stream starts after it, so nothing that changes between loading and going live is missed.
   */
  cursor: z.string().regex(/^\d+$/).nullable().default(null),
  /** Whether this server takes dictation (a speech service is configured). */
  voice: z.object({ enabled: z.boolean() }).default({ enabled: false }),
})
export type Me = z.output<typeof meSchema>

/** Boot check: 200 with `me: null` when signed out (no 401 noise in the browser console). */
export const sessionSchema = z.object({ me: meSchema.nullable() })
export type SessionInfo = z.output<typeof sessionSchema>

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
})
export type ApiError = z.output<typeof apiErrorSchema>

export const clockStateSchema = z.object({
  now: z.iso.datetime(),
  offsetMs: z.number(),
})
export type ClockState = z.output<typeof clockStateSchema>
